<template>
    <TTerminal
        v-if="isAppLoaded && canStart"
        @onMainContainerClick="setAppActive"
    >
        <template #overlay>
            <div
                class="main__overlay"
                v-if="isOverlay"
                @click="isOverlay = false"
            />
        </template>
        <template #header>
            <OHeader />
        </template>
        <template #main>
            <router-view />
        </template>
        <template #footer>
            <OFooter v-if="isShowFooter" />
        </template>
        <template #modal>
            <OModalBase
                v-if="modalOrganism"
                @onClose="closeModal"
                :organism="modalOrganism.component"
                :data="modalOrganism.data"
                :clickOutside="modalOrganism.clickOutside"
            />
        </template>
        <template #player>
            <OPlayer
                v-if="player"
                @onPlayerClick="handlePlayerClick"
            />
        </template>
    </TTerminal>
    <AInitialLoader v-else />
</template>

<script>
import '../../../assets/images/generated-svg';

import OHeader from '../../organisms/o-header/o-header';
import OFooter from '../../organisms/o-footer/o-footer';
import OPlayer from '../../organisms/o-player/o-player';
import TTerminal from '../../templates/t-terminal/t-termianl';
import OModalBase from '../../organisms/o-modal-base/o-modal-base';
import AInitialLoader from '../../atoms/a-initial-loader/a-initial-loader';

import canStart from '../../../utils/mixins/can-start';

import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';

import { fitToScreen } from '../../../utils/helpers/fit-to-screen';
import { SET_GYROSCOPE_STATE } from '../../../shared/components/map-viewer/constants/map-commands';

import {
    START_SCREEN,
    GUIDE_SCREEN,
    SEARCH_SCREEN
} from '../../../utils/constants/pages';

import {
    SHOW_MODAL,
    SHOW_OVERLAY,
    RESTORE_LOCALE,
    SESSION_EXTENDED,
    SESSION_TERMINATED
} from '../../../utils/constants/bus-events-constants';
import {
    UI_MAP_READY,
    UI_ON_CAMERA_UPDATE,
    UI_MAP_CLICKED
} from "../../../shared/components/map-viewer/constants/map-events";
import {
    RESET_TERMINAL_DATA,
    DELAY_BEFORE_PLAYER_START,
    INTERVAL_CHECK_MAP_OBJECTS,
    INTERVAL_CHECK_MAP_DATA_MODIFIED,
    DELAY_BEFORE_CAN_UPDATE_TEMINAL_DATA,
} from '../../../utils/constants/dates-time';

export default {
    name: 'PTerminal',
    mixins: [canStart],
    components: {
        OHeader,
        OFooter,
        OPlayer,
        TTerminal,
        OModalBase,
        AInitialLoader
    },
    data() {
        return {
            player: false,
            isOverlay: false,
            modalOrganism: null,
            activeTimeout: null,
            screensaverTimeout: null,
            checkMapObjectsInterval: null,
            mapDataUpdateCheckInterval: null,
            delayUpdateTerminalDataTimeout: null,
        }
    },
    props: {
        terminalCode: {
            type: String,
            default: null
        },
        mapId: {
            type: String, Number,
            default: null
        }
    },
    computed: {
        ...mapState({
            mapLoading: state => state.mapLoading,
            lastModified: state => state.lastModified,
            dataSourceType: state => state.dataSourceType,
            defaultLocale: state => state.map.meta.language,
            canUpdateMapData: state => state.canUpdateMapData,
            unfilteredMapObjects: state => state.unfilteredMapObjects,
            isMapDataUpdateNeeded: state => state.isMapDataUpdateNeeded,
            resetInterval: state => state.map.meta.reset_interval,
            screensaverTimer: state => state.map.meta.screensaver_delay
        }),
        ...mapGetters([
            'isAppLoaded',
            'getMapConfigProp',
        ]),
        isShowFooter() {
            return ![GUIDE_SCREEN, SEARCH_SCREEN].includes(this.$route.name);
        }
    },
    created() {
        this.initEventListeners();
        this.$store.commit('setActiveMapTheme', 'terminal');
    },
    async mounted() {
        !this.$route.query.terminalMode && fitToScreen();
        this.setTerminalCode(this.terminalCode);
        await this.fetchMapData(this.mapId);

        if(this.dataSourceType === 'CDN'){
            this.mapDataUpdateCheckInterval = setInterval(
                () => this.checkDoesMapDataUpdateNeeded(this.mapId),
                this.getMapConfigProp('features.reactivity.check_interval', INTERVAL_CHECK_MAP_DATA_MODIFIED) * 1000
            );
        }
    },
    beforeDestroy() {
        this.deactivateEventListeners();
        clearTimeout(this.activeTimeout);
        clearTimeout(this.delayUpdateTerminalDataTimeout)
        clearInterval(this.mapDataUpdateCheckInterval)
    },
    watch: {
        mapLoading(value) {
            if (value) this.disableGyroscope();
        },
        canUpdateMapData(value) {
            if  (value) {
                this.maybeUpdateMapData()
                this.pushMapObjects(this.unfilteredMapObjects)
                this.checkMapObjectsInterval = setInterval(() => {
                    this.pushMapObjects(this.unfilteredMapObjects)
                }, INTERVAL_CHECK_MAP_OBJECTS)
            } else {
                clearInterval(this.checkMapObjectsInterval)
            }
        },
        isMapDataUpdateNeeded: 'maybeUpdateMapData',
    },
    methods: {
        ...mapActions([
            'fetchMapData',
            'generateSessionId',
            'checkDoesMapDataUpdateNeeded'
        ]),
        ...mapMutations([
            'setMapLoading',
            'pushMapObjects',
            'setTerminalCode',
            'setCanUpdateMapData',
            'setIsMapDataUpdateNeeded',
        ]),
        initEventListeners() {
            this.$bus.$on(SHOW_MODAL, this.showModal);
            this.$bus.$on(SHOW_OVERLAY, this.showOverlay);
            this.$bus.$on(UI_MAP_CLICKED, this.setAppActive);
            this.$bus.$on(SESSION_EXTENDED,this.setAppActive);
            this.$bus.$on(UI_MAP_READY, this.mapReadyActions);
            this.$bus.$on(SESSION_TERMINATED, this.turnOnPlayerWithDelay);
        },
        deactivateEventListeners() {
            this.$bus.$off(SHOW_MODAL, this.showModal);
            this.$bus.$off(SHOW_OVERLAY, this.showOverlay);
            this.$bus.$off(UI_MAP_CLICKED, this.setAppActive);
            this.$bus.$off(SESSION_EXTENDED,this.setAppActive);
            this.$bus.$off(UI_MAP_READY, this.mapReadyActions);
            this.$bus.$off(UI_ON_CAMERA_UPDATE, this.setAppActive);
            this.$bus.$off(SESSION_TERMINATED, this.turnOnPlayerWithDelay);
        },
        maybeUpdateMapData(){
            this.canUpdateMapData &&
                this.isMapDataUpdateNeeded &&
                this.dataSourceType === 'CDN' &&
                this.fetchMapData(this.mapId);
        },
        showModal(data) {
            this.modalOrganism = data;
        },
        closeModal() {
            this.modalOrganism = null;
        },
        showOverlay(val) {
            this.isOverlay = val;
        },
        turnOnPlayerWithDelay() {
            this.screensaverTimeout = setTimeout(()=> {
                this.turnOnPlayer();
            }, this.screensaverTimer*1000 || DELAY_BEFORE_PLAYER_START)
        },
        turnOnPlayer() {
            this.closeModal();
            this.$bus.$emit(RESTORE_LOCALE, true);
            this.player = true;
            clearTimeout(this.activeTimeout);
            this.delayUpdateTerminalData()
        },
        handlePlayerClick() {
            this.player = false;
            this.setAppActive();
            clearTimeout(this.delayUpdateTerminalDataTimeout)
            this.setCanUpdateMapData(false)
        },
        setAppActive() {
            clearTimeout(this.activeTimeout);
            clearTimeout(this.screensaverTimeout);
            this.activeTimeout = setTimeout(this.timeoutAction, this.resetInterval * 1000 || RESET_TERMINAL_DATA);
        },
        timeoutAction() {
            if (this.$route.name === START_SCREEN) {
                this.turnOnPlayer();
                clearTimeout(this.activeTimeout);
                this.generateSessionId()
            } else {
                this.modalOrganism = {
                    component: 'notification'
                };
            }
        },
        mapReadyActions() {
            this.setAppActive();
            this.disableGyroscope();
            this.$bus.$on(UI_ON_CAMERA_UPDATE, this.setAppActive);
        },
        disableGyroscope() {
            this.$bus.$emit(SET_GYROSCOPE_STATE, false);
        },
        delayUpdateTerminalData() {
            this.delayUpdateTerminalDataTimeout =  setTimeout(() => {
                if(this.player) this.setCanUpdateMapData(true)
            }, DELAY_BEFORE_CAN_UPDATE_TEMINAL_DATA*1000)
        }
    }
}
</script>

<style lang="scss">
@import "p-terminal";
</style>
